import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { dateControlRect, getEdgeAwarePosition } from '../utils/getEdgeAwarePosition';
import { announceDatePopover, onOtherDatePopover } from '../../../../../ui/DateTimePopover/popoverBus';

export function useDueDatePickerState(task) {
  const [openPicker, setOpenPicker] = useState(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [tooltipField, setTooltipField] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const valueRefs = useRef({});
  const portalRef = useRef(null);
  const popoverId = `dates-${task?.id || 'task'}-${openPicker || ''}`;

  const handleFieldClick = useCallback(
    (field) => (e) => {
      e.stopPropagation();

      const pointerRect = {
        left: e.clientX,
        right: e.clientX,
        top: e.clientY,
        bottom: e.clientY,
      };

      if (field.disabledInTemplate && field.templateTooltip) {
        setTooltipPosition(getEdgeAwarePosition(pointerRect, 250, 80));
        setTooltipField(field.id);
        return;
      }

      if (!field.handler) return;

      const popupHeight = field.showTime ? 220 : 180;
      setPickerPosition(getEdgeAwarePosition(pointerRect, 280, popupHeight));

      announceDatePopover(`dates-${task?.id || 'task'}-${field.id}`);
      setOpenPicker(field.id);
    },
    [task?.id]
  );

  const handleFieldMouseLeave = useCallback(
    (field) => () => {
      if (tooltipField === field.id) {
        setTooltipField(null);
      }
    },
    [tooltipField]
  );

  const handleDateChange = useCallback(
    (field) => (date) => {
      if (!date) return;
      const isoDate = field.showTime ? date.toISOString() : date.toISOString().split('T')[0];
      field.handler(task.id, isoDate);
    },
    [task.id]
  );

  const handleClear = useCallback(
    (field) => () => {
      field.handler(task.id, null);
      setOpenPicker(null);
    },
    [task.id]
  );

  useEffect(() => {
    if (!openPicker) return;

    const handleClickOutside = (e) => {
      if (portalRef.current?.contains(e.target)) return;
      if (valueRefs.current[openPicker]?.contains(e.target)) return;
      setOpenPicker(null);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [openPicker]);

  useEffect(() => {
    if (!openPicker) return undefined;
    return onOtherDatePopover(popoverId, () => setOpenPicker(null));
  }, [openPicker, popoverId]);

  useLayoutEffect(() => {
    if (!openPicker) return undefined;
    const place = () => {
      const anchor = valueRefs.current[openPicker];
      const pop = portalRef.current;
      const rect = dateControlRect(anchor);
      if (!rect) return;
      const width = pop?.offsetWidth || 280;
      const height = pop?.offsetHeight || 200;
      setPickerPosition(getEdgeAwarePosition(rect, width, height));
    };
    place();
    const pop = portalRef.current;
    const observer = pop && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(place)
      : null;
    if (pop && observer) observer.observe(pop);
    window.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [openPicker]);

  useEffect(() => {
    if (!openPicker) return;

    const handlePanStart = () => setOpenPicker(null);
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenPicker(null);
    };

    window.addEventListener('flowchartpanstart', handlePanStart);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('flowchartpanstart', handlePanStart);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openPicker]);

  const closePicker = useCallback(() => setOpenPicker(null), []);

  return {
    openPicker,
    closePicker,
    pickerPosition,
    tooltipField,
    tooltipPosition,
    valueRefs,
    portalRef,
    setTooltipField,
    handleFieldClick,
    handleFieldMouseLeave,
    handleDateChange,
    handleClear,
  };
}
